import isString from 'lodash/isString';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Form from '@spothero/ui/form';
import FormElementError from '@spothero/ui/form-element-error';
import FormGroup from '@spothero/ui/form-group';
import Label from '@spothero/ui/label';
import Select from '@spothero/ui/select';
import APIUtils from '@spothero/utils/api';
import VehicleAPI from 'api/vehicle';

class VehicleSelect extends Component {
    static propTypes = {
        className: PropTypes.string,
        onChange: PropTypes.func,
        useLabels: PropTypes.bool,
        setOversizeFeeError: PropTypes.func,
        errorsInline: PropTypes.bool
    };
    static defaultProps = {
        useLabels: true
    };

    constructor(props) {
        super(props);

        const {
            useLabels
        } = props;

        this._yearsByModel = [];
        this._defaultYears = [
            {
                value: '',
                label: (!useLabels) ? 'Year' : '----------'
            }
        ];
        this._defaultMakes = [
            {
                value: '',
                label: (!useLabels) ? 'Make' : '----------'
            }
        ];
        this._defaultModels = [
            {
                value: '',
                label: (!useLabels) ? 'Model' : '----------'
            }
        ];

        this.state = {
            year: '',
            make: '',
            model: '',
            makeLabel: '',
            modelLabel: '',
            years: this._defaultYears,
            makes: this._defaultMakes,
            models: this._defaultModels,
            yearLoading: true,
            makeLoading: false,
            modelLoading: false,
            yearError: null,
            makeError: null,
            modelError: null
        };
    }

    componentDidMount() {
        this._getYearOptions();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            onChange
        } = this.props;

        const {
            make,
            model,
            year,
            makeLabel,
            modelLabel
        } = this.state;

        if (onChange && (prevState.make !== make || prevState.model !== model || prevState.year !== year)) {
            onChange({
                make,
                model,
                year,
                makeLabel,
                modelLabel
            });
        }
    }

    componentWillUnmount() {
        if (this._yearOptionsLoader) {
            this._yearOptionsLoader.cancel();
            this._yearOptionsLoader = null;
        }

        if (this._makeOptionsLoader) {
            this._makeOptionsLoader.cancel();
            this._makeOptionsLoader = null;
        }

        if (this._modelOptionsLoader) {
            this._modelOptionsLoader.cancel();
            this._modelOptionsLoader = null;
        }
    }

    _onYearChange = ({label, value}) => {
        this.setState({
            year: value,
            make: '',
            model: '',
            makes: this._defaultMakes,
            models: this._defaultModels,
            makeLoading: true,
            yearError: null
        });

        this._getMakeOptions({
            year: value
        });
    }

    _onYearError = () => {
        this.setState({
            yearError: 'Please select a year.'
        });
    }

    _onMakeChange = ({label, value}) => {
        this.setState({
            make: value,
            makeLabel: label,
            model: '',
            models: this._defaultModels,
            modelLoading: true,
            makeError: null
        });

        this._getModelOptions({make: value});
    }

    _onMakeError = () => {
        this.setState({
            makeError: 'Please select a make.'
        });
    }

    _onModelChange = ({label, value}) => {
        this.setState({
            model: value,
            modelLabel: label,
            modelError: null
        });
    }

    _onModelError = (error = null) => {
        this.setState({
            modelError: (isString(error)) ? error : 'Please select a model.'
        });
    }

    async _getYearOptions() {
        try {
            this._yearOptionsLoader = APIUtils.makeCancelable(
                VehicleAPI.getYears()
            );

            const response = await this._yearOptionsLoader.promise;
            const years = this._defaultYears.slice();

            if (response.data && response.data.data) {
                response.data = response.data.data;
            }

            response.data.results.forEach(newYear => {
                years.push({
                    value: newYear.toString(),
                    label: newYear.toString()
                });
            });

            this.setState({
                years,
                yearLoading: false
            });
        } catch (error) {
            // years didnt load :(
        }

        this._yearOptionsLoader = null;
    }

    async _getMakeOptions({year, callback}) {
        try {
            this._makeOptionsLoader = APIUtils.makeCancelable(
                VehicleAPI.getMakes(year)
            );

            const response = await this._makeOptionsLoader.promise;
            const makes = this._defaultMakes.slice();

            if (response.data && response.data.data) {
                response.data = response.data.data;
            }

            response.data.results.forEach(make => {
                makes.push({
                    value: make.nice_name,
                    label: make.name
                });
            });

            this.setState({
                makes,
                makeLoading: false
            });
        } catch (error) {
            this.setState({
                makeLoading: false
            });
        }

        this._makeOptionsLoader = null;
    }

    async _getModelOptions({make, callback}) {
        const {
            setOversizeFeeError
        } = this.props;

        try {
            this._modelOptionsLoader = APIUtils.makeCancelable(
                VehicleAPI.getModels(make, this.state.year)
            );

            const response = await this._modelOptionsLoader.promise;
            const models = this._defaultModels.slice();

            if (response.data && response.data.data) {
                response.data = response.data.data;
            }

            response.data.results.forEach(model => {
                models.push({
                    value: model.name,
                    label: model.name
                });
            });

            this.setState({
                models,
                modelLoading: false
            });
        } catch (error) {
            this.setState({
                modelLoading: false
            });

            if (setOversizeFeeError) {
                setOversizeFeeError('We are unable to check for oversized fees at this time. Please continue to checkout and update your vehicle information before you park.');
            } else {
                this._onModelError('Could not get models.');
            }
        }

        this._modelOptionsLoader = null;
    }

    submit() {
        return this._vehicleSelect.submit();
    }

    render() {
        const {
            className,
            useLabels,
            errorsInline
        } = this.props;
        const {
            make,
            model,
            year,
            makes,
            models,
            years,
            makeLoading,
            modelLoading,
            yearLoading,
            makeError,
            modelError,
            yearError
        } = this.state;
        const classes = classNames(
            'VehicleSelect',
            className
        );

        return (
            <div className={classes}>
                <Form
                    ref={node => { this._vehicleSelect = node; }}
                    id="VehicleSelect-form"
                >
                    <FormGroup>
                        <Select
                            name="year"
                            className="VehicleSelect-year"
                            onChange={this._onYearChange}
                            defaultValue={year}
                            disabled={(years.length < 2)}
                            items={years}
                            loading={yearLoading}
                            label={(useLabels) ? <Label>Year</Label> : null}
                            onInvalid={(!errorsInline) ? this._onYearError : null}
                            required
                        />
                        <Select
                            name="make"
                            className="VehicleSelect-make"
                            onChange={this._onMakeChange}
                            defaultValue={make}
                            disabled={(makes.length < 2)}
                            items={makes}
                            loading={makeLoading}
                            label={(useLabels) ? <Label>Make</Label> : null}
                            onInvalid={(!errorsInline) ? this._onMakeError : null}
                            required
                        />
                        <Select
                            name="model"
                            className="VehicleSelect-model"
                            onChange={this._onModelChange}
                            defaultValue={model}
                            disabled={(models.length < 2)}
                            items={models}
                            loading={modelLoading}
                            label={(useLabels) ? <Label>Model</Label> : null}
                            onInvalid={(!errorsInline) ? this._onModelError : null}
                            required
                        />
                        {makeError &&
                            <FormElementError>{makeError}</FormElementError>
                        }
                        {modelError &&
                            <FormElementError>{modelError}</FormElementError>
                        }
                        {yearError &&
                            <FormElementError>{yearError}</FormElementError>
                        }
                    </FormGroup>
                </Form>
            </div>
        );
    }
}

export default VehicleSelect;
